import type { ShiftSummary } from "@kopi/contracts";
import { DEFAULT_SHIFT_SORT, SHIFT_SORTS, type SortValue } from "@kopi/contracts/client";
import { SHIFT_STATUSES, type ShiftStatus } from "@kopi/contracts/codes";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { EyeIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { ShiftStatusBadge, type ShiftsT, STATUS_KEYS, varianceText } from "@/components/shifts";
import { shifts } from "@/i18n/messages/shifts";
import { useI18n, useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { meScopesQuery, outletShiftsQuery, outletsQuery } from "@/lib/queries";
import { sortParam, urlSorting } from "@/lib/sorting";

/** Radix Select items can't have an empty value, so "all statuses" has its own. */
const ALL = "all";

/** The status filter and the column sort (R1-09d) live in the URL, so a reload keeps them. */
function validateSearch(search: Record<string, unknown>): {
  status?: ShiftStatus;
  sort?: SortValue<(typeof SHIFT_SORTS)[number]>;
} {
  const status = SHIFT_STATUSES.find((s) => s === search.status);
  const sort = sortParam(SHIFT_SORTS, search.sort);
  return { ...(status ? { status } : {}), ...(sort ? { sort } : {}) };
}

export const Route = createFileRoute("/_authed/shifts/")({
  staticData: { section: "workspace", crumb: "nav.shifts" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: ShiftsPage,
});

function Money(props: { amount: string | null }) {
  const { format } = useI18n();
  return (
    <span className="tabular-nums">{props.amount === null ? "—" : format.money(BigInt(props.amount))}</span>
  );
}

function Variance(props: { variance: string | null }) {
  const { t, format } = useMessages(shifts);
  if (props.variance === null) return <span className="text-muted-foreground">—</span>;
  const amount = BigInt(props.variance);
  return (
    <span className={amount < 0n ? "text-destructive" : amount > 0n ? "text-warning" : ""}>
      {varianceText(t, format.money, props.variance)}
    </span>
  );
}

function When(props: { iso: string }) {
  const { format } = useI18n();
  return <time dateTime={props.iso}>{format.dateTime(props.iso)}</time>;
}

const columnsFor = (t: ShiftsT): DataTableColumn<ShiftSummary>[] => [
  {
    id: "shift",
    header: t("shifts.register"),
    meta: { card: "title" },
    cell: ({ row }) => (
      <Link
        to="/shifts/$shiftId"
        params={{ shiftId: row.original.id }}
        className="flex min-w-0 flex-col rounded-md hover:underline"
      >
        <span className="truncate font-medium">{row.original.registerName}</span>
        <span className="truncate text-muted-foreground text-sm">
          {t("shifts.number", { number: row.original.number })}
        </span>
      </Link>
    ),
  },
  {
    id: "opened",
    header: t("shifts.opened"),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <When iso={row.original.openedAt} />
        <span className="truncate text-muted-foreground text-sm">{row.original.openedBy}</span>
      </div>
    ),
  },
  {
    id: "status",
    header: t("shifts.status"),
    meta: { className: "md:w-44" },
    cell: ({ row }) => <ShiftStatusBadge status={row.original.status} />,
  },
  {
    id: "counted",
    header: t("shifts.countedLabel"),
    meta: { align: "end" },
    cell: ({ row }) => <Money amount={row.original.countedCash} />,
  },
  {
    id: "variance",
    header: t("shifts.variance"),
    meta: { align: "end" },
    cell: ({ row }) => <Variance variance={row.original.variance} />,
  },
];

/** Every register's shifts at an outlet (R1-15), for whoever approves them there. */
function ShiftsPage() {
  const { t } = useMessages(shifts);
  const columns = useMemo(() => columnsFor(t), [t]);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const approvable = outlets.filter((o) => canAt(scopes, "cash.shift.approve", o.id));
  // Starts at the shell's outlet when this user approves its shifts.
  const [outletId, setOutletId] = useState(
    approvable.find((o) => o.id === current?.id)?.id ?? approvable[0]?.id ?? "",
  );
  const { status, sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sorting = urlSorting(SHIFT_SORTS, DEFAULT_SHIFT_SORT, sort, (next) => {
    void navigate({
      search: (prev) => ({
        ...(prev.status ? { status: prev.status } : {}),
        ...(next ? { sort: next } : {}),
      }),
      replace: true,
    });
  });
  const list = useInfiniteQuery(outletShiftsQuery(outletId, status, sort));

  if (approvable.length === 0) {
    return (
      <Page>
        <PageHeader title={t("shifts.listTitle")} subtitle={t("shifts.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={t("shifts.listTitle")} subtitle={t("shifts.listSubtitle")} />
      <DataTable
        paging="pages"
        label={t("shifts.list")}
        columns={columns}
        query={list}
        getRowId={(shift) => shift.id}
        {...sorting}
        filters={
          <>
            {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
            <OptionSelect
              aria-label={t("outlet.heading")}
              className="md:w-56"
              options={approvable.map((o) => ({ value: o.id, label: o.name }))}
              value={outletId}
              onValueChange={setOutletId}
            />
            <OptionSelect
              aria-label={t("shifts.status")}
              className="md:w-56"
              options={[
                { value: ALL, label: t("shifts.status.all") },
                ...SHIFT_STATUSES.map((s) => ({ value: s, label: t(STATUS_KEYS[s]) })),
              ]}
              value={status ?? ALL}
              onValueChange={(value) =>
                void navigate({
                  search: (prev) => ({
                    ...(prev.sort ? { sort: prev.sort } : {}),
                    ...(value === ALL ? {} : { status: value as ShiftStatus }),
                  }),
                  replace: true,
                })
              }
            />
          </>
        }
        rowActions={(shift) => [
          {
            label: t("shifts.view"),
            icon: <EyeIcon />,
            onSelect: () => void navigate({ to: "/shifts/$shiftId", params: { shiftId: shift.id } }),
          },
        ]}
        empty={{ title: t("shifts.emptyTitle"), description: t("shifts.emptyHint") }}
      />
    </Page>
  );
}
